import { API_URL, APP_URL } from './links';

/*
 * Ambassador referral links: https://www.userevaluation.com/?ref=<code> (any page). The first code a
 * browser sees is kept with no time limit (localStorage, refreshed into a `ue_ref` cookie on
 * .userevaluation.com that the app reads too), the click is counted once, and every link to the
 * app's sign-up carries `?ref=` so a sign-up months later still counts.
 */

const KEY = 'ue_ref';
const CODE = /^[a-z0-9-]{3,64}$/;
/** Browsers cap cookies at about 400 days; each visit sets it again. */
const COOKIE_AGE = 400 * 86_400;

const clean = (v: string | null | undefined) => {
  const c = v?.trim().toLowerCase() ?? '';
  return CODE.test(c) ? c : null;
};

function readCookie() {
  const m = document.cookie.match(/(?:^|;\s*)ue_ref=([^;]+)/);
  return m ? clean(decodeURIComponent(m[1]!)) : null;
}

function writeCookie(code: string) {
  const host = window.location.hostname;
  const domain = host === 'userevaluation.com' || host.endsWith('.userevaluation.com') ? '; domain=.userevaluation.com' : '';
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${KEY}=${encodeURIComponent(code)}; path=/; max-age=${COOKIE_AGE}; samesite=lax${domain}${secure}`;
}

export function storedReferral(): string | null {
  try {
    return clean(localStorage.getItem(KEY)) ?? readCookie();
  } catch {
    return readCookie();
  }
}

/** Keep the first `?ref=` this browser saw and count the click once; refresh the shared cookie. */
export function captureReferral(search: string) {
  const fromUrl = clean(new URLSearchParams(search).get('ref'));
  const kept = storedReferral();
  const code = kept ?? fromUrl;
  if (!code) return;
  try { localStorage.setItem(KEY, code); } catch { /* private mode: the cookie still has it */ }
  writeCookie(code);
  if (!kept && fromUrl) {
    void fetch(`${API_URL}/api/public/referral-click`, {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code: fromUrl }), keepalive: true,
    }).catch(() => {});
  }
}

/** Links to the app's sign-up carry the kept code (`?ref=`), unless they already have one. */
export function withReferral(href: string): string {
  const code = storedReferral();
  if (!code || !href.startsWith(`${APP_URL}/signup`)) return href;
  const url = new URL(href);
  if (!url.searchParams.has('ref')) url.searchParams.set('ref', code);
  return url.toString();
}

let listening = false;
/** One capture-phase listener: rewrite a sign-up link just before it's followed (clicks, middle-clicks, keyboard). */
export function listenForSignupLinks() {
  if (listening) return;
  listening = true;
  const rewrite = (e: Event) => {
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (a) a.href = withReferral(a.href);
  };
  document.addEventListener('click', rewrite, true);
  document.addEventListener('auxclick', rewrite, true);
}
